// Enhanced Section Component with Liquid Glass Effects
// Premium section with cinematic styling for 2026 anime streaming experience

import * as React from 'react';
import {
  Box,
  Typography,
  Stack,
} from '@mui/material';
import { motion } from 'framer-motion';
import { designTokens } from '../../theme/tokens';

interface EnhancedSectionProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  id?: string;
  variant?: 'default' | 'glass' | 'glass-elevated' | 'glowing';
  animateChildren?: boolean;
}

export const EnhancedSection = ({
  title,
  subtitle,
  action,
  children,
  className,
  id,
  variant = 'glass',
  animateChildren = true,
}: EnhancedSectionProps) => {
  // Get theme colors for dynamic styling
  const getGlassBackground = () => {
    // Using CSS variables for theme-aware colors
    return 'rgba(17, 24, 39, 0.3)'; // Dark mode glass
  };

  const getGlassBorder = () => {
    return '1px solid rgba(255, 255, 255, 0.08)';
  };

  const getGlassHoverBackground = () => {
    return 'rgba(17, 24, 39, 0.4)';
  };

  const getGlassHoverBorder = () => {
    return '1px solid rgba(255, 255, 255, 0.12)';
  };

  // Define variants
  const variantStyles: Record<string, any> = {
    default: {
      position: 'relative',
      overflow: 'hidden',
      background: 'transparent',
    },
    glass: {
      position: 'relative',
      overflow: 'hidden',
      background: 'rgba(17, 24, 39, 0.25)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: 24,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.3), transparent)',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(236, 72, 153, 0.3), transparent)',
      },
      '&:hover': {
        background: 'rgba(17, 24, 39, 0.35)',
        backdropFilter: 'blur(25px)',
        WebkitBackdropFilter: 'blur(25px)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 32px rgba(31, 38, 135, 0.25)',
      },
    },
    'glass-elevated': {
      position: 'relative',
      overflow: 'hidden',
      background: 'rgba(17, 24, 39, 0.2)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.06)',
      borderRadius: 28,
      boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, rgba(139, 92, 246, 0), rgba(139, 92, 246, 0.4), rgba(139, 92, 246, 0))',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, rgba(236, 72, 153, 0), rgba(236, 72, 153, 0.4), rgba(236, 72, 153, 0))',
      },
      '&:hover': {
        background: 'rgba(17, 24, 39, 0.25)',
        backdropFilter: 'blur(25px)',
        WebkitBackdropFilter: 'blur(25px)',
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(31, 38, 135, 0.45)',
      },
    },
    glowing: {
      position: 'relative',
      overflow: 'hidden',
      background: 'rgba(17, 24, 39, 0.15)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
      borderRadius: 32,
      boxShadow:
        '0 0 0 1px rgba(255, 255, 255, 0.05), ' +
        '0 0 20px rgba(139, 92, 246, 0.1), ' +
        '0 0 40px rgba(236, 72, 153, 0.1)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -2,
        left: -2,
        right: -2,
        bottom: -2,
        background:
          'linear-gradient(45deg, transparent, rgba(139, 92, 246, 0.3), transparent, rgba(236, 72, 153, 0.3), transparent)',
        backgroundSize: '200% 200%',
        animation: 'gradientShift 4s ease infinite',
        borderRadius: 'inherit',
        pointerEvents: 'none',
      },
      '&:hover': {
        background: 'rgba(17, 24, 39, 0.2)',
        backdropFilter: 'blur(25px)',
        WebkitBackdropFilter: 'blur(25px)',
        transform: 'translateY(-4px)',
        boxShadow:
          '0 0 0 1px rgba(255, 255, 255, 0.08), ' +
          '0 0 30px rgba(139, 92, 246, 0.2), ' +
          '0 0 60px rgba(236, 72, 153, 0.2)',
      },
    },
  };

  const getVariantStyle = () => variantStyles[variant] || variantStyles.glass;

  return (
    <motion.div
      id={id}
      className={className}
      style={{
        ...getVariantStyle(),
        position: 'relative',
        marginBottom: 5,
        ...(variant === 'glowing' && {
          '&::before': {
            animation: 'gradientShift 4s ease infinite',
          },
        }),
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5, ease: [0.175, 0.885, 0.32, 1.275] }}
        style={{ marginBottom: 3 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            position: 'relative',
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                letterSpacing: '-0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                background: 'linear-gradient(90deg, #8b5cf6, #ec4899, #f59e0b)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              }}
            >
              {title}
              {/* Add animated underline effect */}
              <span
                style={{
                  display: 'block',
                  height: '4px',
                  width: '100%',
                  background:
                    'linear-gradient(90deg, transparent, #8b5cf6, #ec4899, #f59e0b, transparent)',
                  backgroundSize: '200% 100%',
                  animation: 'gradientShift 3s ease infinite',
                  borderRadius: 2,
                  marginTop: 4,
                }}
              />
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
      </motion.div>

      {/* Content container with optional stagger animation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={animateChildren ? {
          '& > *': {
            opacity: 0,
            transform: 'translateY(20px)',
            transition: {
              delay: (i: number) => i * 0.05,
              duration: 0.4,
              ease: [0.175, 0.885, 0.32, 1.275],
            },
          }
        } : {}}
      >
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
              xl: 'repeat(5, 1fr)',
            },
          }}
        >
          {children}
        </Box>
      </motion.div>
    </motion.div>
  );
};

// Add keyframes for gradient animation if not already defined
const style = `
  @keyframes gradientShift {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

export default EnhancedSection;